import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type { ContactTodo } from '@/lib/types'

export function useContactTodos(contactId: string | undefined) {
  return useQuery({
    queryKey: ['todos', contactId],
    queryFn: () =>
      api<{ items: ContactTodo[]; total: number }>(`/api/contacts/${contactId}/todos`).then(
        (r) => r.items,
      ),
    enabled: !!contactId,
  })
}

export function useCreateTodo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, ...data }: { contactId: string; title: string; due_date?: string }) =>
      api<ContactTodo>(`/api/contacts/${contactId}/todos`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['todos', variables.contactId] })
      toast.success(i18n.t('toast.todoCreated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useUpdateTodo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      contactId?: string
      title?: string
      completed?: boolean
      due_date?: string
      sort_order?: number
    }) => api<ContactTodo>(`/api/todos/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onMutate: async (variables) => {
      await qc.cancelQueries({ queryKey: ['todos'] })

      const allTodoQueries = qc.getQueriesData<ContactTodo[]>({ queryKey: ['todos'] })
      const previousData = new Map(allTodoQueries)

      qc.setQueriesData<ContactTodo[]>({ queryKey: ['todos'] }, (old) => {
        if (!old) return old
        return old.map((todo) =>
          todo.id === variables.id
            ? {
                ...todo,
                ...variables,
                completedAt:
                  variables.completed !== undefined
                    ? variables.completed
                      ? new Date().toISOString()
                      : null
                    : todo.completedAt,
              }
            : todo,
        )
      })

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          qc.setQueryData(key, data)
        }
      }
      toast.error(i18n.t('toast.error'))
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}

export function useDeleteTodo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/todos/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['todos'] })
      toast.success(i18n.t('toast.todoDeleted'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
